import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Select, Input, message } from 'antd';
import { debounce } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import Cascader from 'antd-mobile/es/components/cascader';
import type { CascaderOption } from 'antd-mobile/es/components/cascader-view';
import SearchBar from 'antd-mobile/es/components/search-bar';
import Toast from 'antd-mobile/es/components/toast';
import Arrow from '../../../components/Arrow';
import {
  type AdminDivisionOptionDto,
  type PlaygroundDetailItem,
  getPlaygroundDetailList,
  listPlaygroundProvinceCityOptions,
} from '../../../api/playgroundApi';
import styles from './FilterPanel.module.scss';

/** 浮窗内 Tab 模式 */
export type FilterMode = 'search' | 'region';

interface FilterPanelProps {
  provinceOptions: AdminDivisionOptionDto[];
  cityOptions: AdminDivisionOptionDto[];
  selectedProvince: AdminDivisionOptionDto | null;
  selectedCity: AdminDivisionOptionDto | null;
  lanType: string;
  isMobile: boolean;
  /** 地图正在 loading 时为 true，此时屏蔽浮窗的外部点击关闭，防止误触 loading 遮罩 */
  mapLoading?: boolean;
  /** 已选中的搜索操场（父组件提升管理，PC/H5 共享） */
  selectedSearchItem: PlaygroundDetailItem | null;
  /** 搜索输入关键词（父组件提升管理，PC/H5 共享） */
  searchKeyword: string;
  onSearchKeywordChange: (keyword: string) => void;
  onSelectedSearchItemChange: (item: PlaygroundDetailItem | null) => void;
  /** 当前 tab（父组件提升管理，PC/H5 共享） */
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  onProvinceSelect: (province: AdminDivisionOptionDto) => void;
  onCitySelect: (city: AdminDivisionOptionDto | null) => void;
  onSearchResultClick: (item: PlaygroundDetailItem) => void;
  onClear: () => void;
}

const FilterPanel = (props: FilterPanelProps) => {
  const { t } = useTranslation('common');
  const {
    provinceOptions,
    cityOptions,
    selectedProvince,
    selectedCity,
    lanType,
    isMobile,
    mapLoading,
    selectedSearchItem,
    searchKeyword,
    onSearchKeywordChange,
    onSelectedSearchItemChange,
    filterMode,
    onFilterModeChange,
    onProvinceSelect,
    onCitySelect,
    onSearchResultClick,
    onClear,
  } = props;

  // ===== 共用状态（searchKeyword / selectedSearchItem 已提升至父组件） =====
  const setSearchKeyword = onSearchKeywordChange;
  const setSelectedSearchItem = onSelectedSearchItemChange;
  const [searchResults, setSearchResults] = useState<PlaygroundDetailItem[]>([]);
  const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRequestIdRef = useRef(0);
  // 标记是否处于 IME 组合输入（如拼音输入中），组合期间不触发搜索且不展示结果提示
  const isComposingRef = useRef(false);
  const [isComposing, setIsComposing] = useState(false);

  // ===== PC 浮窗状态 =====
  const [popupVisible, setPopupVisible] = useState(false);
  // mode 已提升至父组件（filterMode prop）
  const mode = filterMode;
  const setMode = onFilterModeChange;
  const panelRef = useRef<HTMLDivElement>(null);
  // 用 ref 在事件回调中访问最新的 props 值，避免 useEffect 闭包捕获旧值
  const mapLoadingRef = useRef(mapLoading);
  mapLoadingRef.current = mapLoading;

  // ===== H5 Cascader 状态 =====
  const [cascaderVisible, setCascaderVisible] = useState(false);
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const [cascaderValue, setCascaderValue] = useState<string[]>([]);
  // 保存每个省份下城市的完整数据（含 count），用于 confirm 时传递
  const cityDataMapRef = useRef<Record<string, AdminDivisionOptionDto[]>>({});

  // 初始化 cascader options（从 provinceOptions 转换，若已选省份且有城市数据则同步进去）
  useEffect(() => {
    if (!isMobile) return;
    const options = provinceOptions.map((p) => {
      const label = p.name;
      // 如果当前省份已选且有城市数据，直接填充城市列表
      if (selectedProvince && p.adCode === selectedProvince.adCode && cityOptions.length > 0) {
        const cities = cityOptions.map((c) => ({
          label: c.name,
          value: c.adCode,
        }));
        // 同时缓存到 cityDataMapRef
        cityDataMapRef.current[p.adCode] = cityOptions;
        return {
          label,
          value: p.adCode,
          children: [{ label: t('playgroundMapConfig.allCities'), value: 'all' }, ...cities] as CascaderOption[],
        };
      }
      return {
        label,
        value: p.adCode,
        children: [{ label: t('playgroundMapConfig.allCities'), value: 'all' }] as CascaderOption[],
      };
    });
    setCascaderOptions(options);
  }, [provinceOptions, isMobile, selectedProvince, cityOptions]);

  // 同步外部选中状态到 cascaderValue
  useEffect(() => {
    if (!isMobile) return;
    const val: string[] = [];
    if (selectedProvince) val.push(selectedProvince.adCode);
    if (selectedCity) val.push(selectedCity.adCode);
    setCascaderValue(val);
  }, [selectedProvince, selectedCity, isMobile]);

  // 防抖搜索请求
  // 请求版本号在输入时即将递增（见 handleH5SearchChange/handlePCInputChange），
  // 这里只需拍照快照记录当前版本号，响应回来后对比是否过期。
  const debouncedSearchRequest = useMemo(
    () =>
      debounce(async (keyword: string, lang: string) => {
        if (!keyword.trim()) {
          setIsSearching(false);
          setSearchResults([]);
          setSearchDropdownVisible(false);
          return;
        }
        // 快照当前版本号（已在输入时递增）
        const requestId = searchRequestIdRef.current;
        try {
          const res = await getPlaygroundDetailList({
            playgroundName: keyword.trim(),
            lanType: lang,
            pageSize: 4,
            pageNo: 1,
          });
          // 版本号不匹配说明有更新的请求已发出，丢弃这个过期响应
          if (requestId !== searchRequestIdRef.current) return;
          const results = (res.data?.pageContent?.slice(0, 4) || []) as unknown as PlaygroundDetailItem[];
          setSearchResults(results);
          setSearchDropdownVisible(results.length > 0);
        } catch (err: any) {
          if (requestId !== searchRequestIdRef.current) return;
          // 业务错误（code 非 0）时优先展示后端 message，否则展示通用失败提示
          const errMsg = err?.message || t('playgroundMapConfig.searchFailed');
          if (isMobile) {
            Toast.show({ content: errMsg, position: 'bottom' });
          } else {
            message.error(errMsg);
          }
          setSearchResults([]);
          setSearchDropdownVisible(false);
        } finally {
          if (requestId === searchRequestIdRef.current) {
            setIsSearching(false);
          }
        }
      }, 300),
    [isMobile],
  );

  useEffect(() => {
    return () => {
      debouncedSearchRequest.cancel();
    };
  }, [debouncedSearchRequest]);

  // PC 端：点击浮窗外部时关闭
  useEffect(() => {
    if (!popupVisible || isMobile) {
      return undefined;
    }
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // 地图正在 loading 时忽略外部点击，防止误触 Spin 遮罩关闭浮窗
        if (mapLoadingRef.current) {
          return;
        }
        setPopupVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [popupVisible, isMobile]);

  // ===== H5 搜索处理 =====
  const handleH5SearchChange = (value: string) => {
    // 用户重新输入时取消已选中状态
    if (selectedSearchItem) {
      setSelectedSearchItem(null);
    }
    setSearchKeyword(value);
    // 组合输入中（如拼音未选字）不触发搜索
    if (isComposingRef.current) return;
    if (value.trim()) {
      // 输入时立即递增版本号，确保旧请求的响应回来时能被正确识别为过期
      searchRequestIdRef.current += 1;
      setIsSearching(true);
    } else {
      searchRequestIdRef.current += 1;
      setIsSearching(false);
      setSearchResults([]);
      setSearchDropdownVisible(false);
    }
    debouncedSearchRequest(value, lanType);
  };

  const handleH5CompositionStart = () => {
    isComposingRef.current = true;
    setIsComposing(true);
  };

  const handleH5CompositionEnd = () => {
    isComposingRef.current = false;
    setIsComposing(false);
    // composition 结束时 searchKeyword 已是最新受控值，直接使用
    if (searchKeyword.trim()) {
      searchRequestIdRef.current += 1;
      setIsSearching(true);
    } else {
      searchRequestIdRef.current += 1;
      setIsSearching(false);
      setSearchResults([]);
      setSearchDropdownVisible(false);
    }
    debouncedSearchRequest(searchKeyword, lanType);
  };

  const handleH5SearchResultClick = (item: PlaygroundDetailItem) => {
    setSelectedSearchItem(item);
    setSearchKeyword(item.pgName);
    setSearchResults([]);
    setSearchDropdownVisible(false);
    onSearchResultClick(item);
  };

  // H5 清除已选搜索操场
  const handleH5ClearSearch = () => {
    setSelectedSearchItem(null);
    setSearchKeyword('');
    setSearchResults([]);
    setSearchDropdownVisible(false);
    onClear();
  };

  // H5 Cascader 选择省时动态加载城市
  const handleCascaderSelect = async (value: string[]) => {
    // 选择了省份，需要加载城市列表
    if (value.length === 1) {
      const provinceCode = value[0];
      const province = provinceOptions.find((p) => p.adCode === provinceCode);
      if (!province) return;

      // 检查是否已经加载过城市（children 为 optionSkeleton 或仅含"全部"说明还未加载）
      const currentOption = cascaderOptions.find((o) => o.value === provinceCode);
      const hasLoadedCities =
        currentOption?.children &&
        currentOption.children !== Cascader.optionSkeleton &&
        currentOption.children.length > 1;
      if (hasLoadedCities) return;

      // 设置 loading 骨架屏
      setCascaderOptions((prev) =>
        prev.map((opt) => (opt.value === provinceCode ? { ...opt, children: Cascader.optionSkeleton } : opt)),
      );

      try {
        // 省市筛选项接口：provinceCode 传省的 adCode（国标编码），不是 id
        const res = await listPlaygroundProvinceCityOptions({ lanType, provinceCode: province.adCode });
        const cityList = res.data || [];
        // 缓存完整城市数据，供 confirm 时传递给父组件
        cityDataMapRef.current[provinceCode] = cityList;
        const cities = cityList.map((c) => ({
          label: c.name,
          value: c.adCode,
        }));
        setCascaderOptions((prev) =>
          prev.map((opt) =>
            opt.value === provinceCode ? { ...opt, children: [{ label: '全部', value: 'all' }, ...cities] } : opt,
          ),
        );
      } catch {
        // 加载失败时恢复为仅"全部"
        setCascaderOptions((prev) =>
          prev.map((opt) =>
            opt.value === provinceCode ? { ...opt, children: [{ label: '全部', value: 'all' }] } : opt,
          ),
        );
        Toast.show({ content: t('playgroundMapConfig.loadCityFailed'), position: 'bottom' });
      }
    }
  };

  // H5 Cascader 确认选择
  const handleCascaderConfirm = (value: string[]) => {
    setCascaderValue(value);
    if (value.length === 0) {
      onClear();
      return;
    }
    // 地区筛选与操场搜索互斥，清除已选搜索操场
    setSelectedSearchItem(null);
    setSearchKeyword('');
    setSearchResults([]);
    setSearchDropdownVisible(false);
    const province = provinceOptions.find((p) => p.adCode === value[0]);
    if (province) {
      if (value.length >= 2 && value[1] !== 'all') {
        // 选了具体城市：先选省（内部会 setSelectedCity(null)），再选城市
        onProvinceSelect(province);
        // 从缓存的完整城市数据中查找（含 count 等字段）
        const cityList = cityDataMapRef.current[value[0]] || [];
        const cityData = cityList.find((c) => c.adCode === value[1]);
        if (cityData) {
          onCitySelect(cityData);
        } else {
          // 兜底：从 cascader options 中提取基本信息
          const cityOpt = cascaderOptions.find((o) => o.value === value[0]);
          const children = cityOpt?.children;
          if (Array.isArray(children) && children !== Cascader.optionSkeleton) {
            const city = children.find((c) => c.value === value[1]);
            if (city) {
              onCitySelect({ adCode: city.value, name: city.label.replace(/\s*\(\d+\)$/, '') });
            }
          }
        }
      } else {
        // 只选了省份，或选了"全部城市"：只调用 onProvinceSelect
        // onProvinceSelect 内部已包含 setSelectedCity(null) 和拉取地图数据的完整逻辑
        // 不能再额外调用 onCitySelect(null)，否则此时 selectedProvince 尚未更新（setState 异步），
        // 导致 onCitySelect 里 provinceAdCode 为 undefined，接口请求不带省份筛选条件
        onProvinceSelect(province);
      }
    }
  };

  // H5 端获取 Cascader 触发文案
  const getCascaderLabel = () => {
    if (selectedProvince) {
      return `${selectedProvince.name}${selectedCity ? ` · ${selectedCity.name}` : ''}`;
    }
    return t('playgroundMapConfig.selectRegion');
  };

  // ===== PC 端逻辑 =====
  const handleModeChange = (newMode: FilterMode) => {
    if (newMode === mode) return;
    setMode(newMode);
  };

  // PC 清除已选搜索操场（输入框 clear 图标 / 清除按钮 共用）
  const handlePCClearSearch = () => {
    setSelectedSearchItem(null);
    setSearchKeyword('');
    setSearchResults([]);
    setSearchDropdownVisible(false);
    onClear();
  };

  const handlePCInputChange = (value: string) => {
    // 点击输入框清除图标或用户重新输入时：若当前有已选中操场，必须同步重置地图
    if (selectedSearchItem) {
      if (!value.trim()) {
        // 清除内容（点击 clear 图标）：全量清除，包含地图重置
        handlePCClearSearch();
        return;
      }
      setSelectedSearchItem(null);
    }
    setSearchKeyword(value);
    // 组合输入中（如拼音未选字）不触发搜索
    if (isComposingRef.current) return;
    if (value.trim()) {
      // 输入时立即递增版本号，确保旧请求的响应回来时能被正确识别为过期
      searchRequestIdRef.current += 1;
      setIsSearching(true);
    } else {
      searchRequestIdRef.current += 1;
      setIsSearching(false);
      setSearchResults([]);
      setSearchDropdownVisible(false);
    }
    debouncedSearchRequest(value, lanType);
  };

  const handlePCCompositionStart = () => {
    isComposingRef.current = true;
    setIsComposing(true);
  };

  const handlePCCompositionEnd = () => {
    isComposingRef.current = false;
    setIsComposing(false);
    // composition 结束时 searchKeyword 已是最新受控值，直接使用
    if (searchKeyword.trim()) {
      searchRequestIdRef.current += 1;
      setIsSearching(true);
    } else {
      searchRequestIdRef.current += 1;
      setIsSearching(false);
      setSearchResults([]);
      setSearchDropdownVisible(false);
    }
    debouncedSearchRequest(searchKeyword, lanType);
  };

  const handlePCSearchResultClick = (item: PlaygroundDetailItem) => {
    setSelectedSearchItem(item);
    setSearchKeyword(item.pgName);
    setSearchResults([]);
    setSearchDropdownVisible(false);
    onSearchResultClick(item);
  };

  const handleProvinceChange = (adCode: string) => {
    const province = provinceOptions.find((p) => p.adCode === adCode);
    if (province) {
      // 地区筛选与操场搜索互斥，清除已选搜索操场
      setSelectedSearchItem(null);
      setSearchKeyword('');
      setSearchResults([]);
      setSearchDropdownVisible(false);
      onProvinceSelect(province);
    }
  };

  const handleCityChange = (adCode: string) => {
    // 地区筛选与操场搜索互斥，清除已选搜索操场
    setSelectedSearchItem(null);
    setSearchKeyword('');
    setSearchResults([]);
    setSearchDropdownVisible(false);
    if (adCode === 'all') {
      onCitySelect(null);
    } else {
      const city = cityOptions.find((c) => c.adCode === adCode);
      if (city) {
        onCitySelect(city);
      }
    }
  };

  const handleClearRegion = () => {
    onClear();
  };

  // PC 触发按钮文案
  const getTriggerLabel = () => {
    // 操场搜索模式：已选中操场时显示操场名称
    if (filterMode === 'search' && selectedSearchItem) {
      return selectedSearchItem.pgName;
    }
    // 地区筛选模式：已选省份时显示省市名称
    if (selectedProvince) {
      return `${selectedProvince.name}${selectedCity ? ` · ${selectedCity.name}` : ''}`;
    }
    // 默认：显示当前模式占位文案
    return filterMode === 'search' ? t('playgroundMapConfig.tabSearch') : t('playgroundMapConfig.tabRegion');
  };

  const hasActiveFilter = !!(selectedProvince || selectedCity || selectedSearchItem);

  // H5 端切换 Tab（h5Mode 共用父组件的 filterMode）
  const h5Mode = filterMode;
  const setH5Mode = onFilterModeChange;

  const handleH5ModeChange = (newMode: FilterMode) => {
    if (newMode === h5Mode) return;
    setH5Mode(newMode);
  };

  // 高亮搜索关键词：将 text 中匹配 keyword 的部分用 <mark> 包裹
  const highlightKeyword = (text: string, keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return text;
    // 转义正则特殊字符，避免关键词含特殊字符时报错
    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === trimmed.toLowerCase() ? (
        // eslint-disable-next-line react/no-array-index-key
        <mark key={`highlight-${i}`} className={styles['search-highlight']}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  // 获取已筛选操场数量
  const getFilteredCount = () => {
    if (selectedCity) return selectedCity.count;
    if (selectedProvince) return selectedProvince.count;
    return undefined;
  };

  // ===== H5 端渲染 =====
  const renderH5 = () => (
    <div className={styles['filter-panel-h5']}>
      {/* Tab 切换 */}
      <div className={styles['h5-segmented']}>
        <button
          className={`${styles['h5-seg-item']} ${h5Mode === 'search' ? styles.active : ''}`}
          onClick={() => handleH5ModeChange('search')}
        >
          {t('playgroundMapConfig.tabSearch')}
        </button>
        <button
          className={`${styles['h5-seg-item']} ${h5Mode === 'region' ? styles.active : ''}`}
          onClick={() => handleH5ModeChange('region')}
        >
          {t('playgroundMapConfig.tabRegion')}
        </button>
      </div>
      {/* 内容区 */}
      <div className={styles['h5-content']}>
        {h5Mode === 'search' ? (
          <div
            className={styles['h5-search-wrap']}
            onCompositionStart={handleH5CompositionStart}
            onCompositionEnd={handleH5CompositionEnd}
          >
            <SearchBar
              placeholder={t('playgroundMapConfig.searchPlaceholder')}
              value={searchKeyword}
              onChange={handleH5SearchChange}
              onClear={handleH5ClearSearch}
              maxLength={50}
            />
            {searchKeyword.length > 0 && <div className={styles['input-count']}>{searchKeyword.length}/50</div>}
            {/* 已选中操场回填展示 */}
            {selectedSearchItem && (
              <div className={styles['filter-footer']}>
                <div className={styles['filter-count']}>
                  {t('playgroundMapConfig.selectedLabel')}
                  <span className={styles['filter-count-num']}>{selectedSearchItem.pgName}</span>
                </div>
                <button className={styles['h5-clear-btn']} onClick={handleH5ClearSearch}>
                  {t('playgroundMapConfig.clear')}
                </button>
              </div>
            )}
            {!isComposing && searchKeyword.trim() && !selectedSearchItem && isSearching && (
              <div className={styles['search-empty']}>{t('playgroundMapConfig.searching')}</div>
            )}
            {!isComposing &&
              searchKeyword.trim() &&
              !selectedSearchItem &&
              !isSearching &&
              searchDropdownVisible &&
              searchResults.length > 0 && (
                <div className={styles['h5-search-results']}>
                  {searchResults.map((item) => (
                    <div
                      key={item.pgNo}
                      className={styles['search-item']}
                      onClick={() => handleH5SearchResultClick(item)}
                    >
                      <div className={styles['search-item-name']}>{highlightKeyword(item.pgName, searchKeyword)}</div>
                      <div className={styles['search-item-meta']}>
                        <span className={styles['search-item-address']}>{item.pgAddress}</span>
                        <span className={styles['search-item-status']}>· {item.pgStatusName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            {!isComposing &&
              searchKeyword.trim() &&
              !selectedSearchItem &&
              !isSearching &&
              searchResults.length === 0 && (
                <div className={styles['search-empty']}>{t('playgroundMapConfig.noResult')}</div>
              )}
          </div>
        ) : (
          <div className={styles['h5-region-wrap']}>
            <button
              className={`${styles['h5-region-btn']} ${hasActiveFilter ? styles.active : ''} ${
                !hasActiveFilter ? styles.placeholder : ''
              }`}
              onClick={() => setCascaderVisible(true)}
            >
              <span className={styles['h5-region-label']}>{getCascaderLabel()}</span>
              <Arrow className={styles['h5-arrow-icon']} />
            </button>
            {hasActiveFilter && (
              <div className={styles['filter-footer']}>
                <div className={styles['filter-count']}>
                  {getFilteredCount() === undefined ? (
                    <span className={styles['filter-count-loading']}>{t('playgroundMapConfig.filtering')}</span>
                  ) : (
                    <span>
                      {(t('playgroundMapConfig.filteredCount') as string).replace(
                        '{{count}}',
                        String(getFilteredCount()),
                      )}
                    </span>
                  )}
                </div>
                <button
                  className={styles['h5-clear-btn']}
                  onClick={() => {
                    onClear();
                    setCascaderValue([]);
                  }}
                >
                  {t('playgroundMapConfig.clear')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* antd-mobile Cascader */}
      <Cascader
        options={cascaderOptions}
        visible={cascaderVisible}
        onClose={() => setCascaderVisible(false)}
        value={cascaderValue}
        onSelect={handleCascaderSelect}
        onConfirm={handleCascaderConfirm}
        title={t('playgroundMapConfig.selectRegion')}
        cancelText={t('playgroundMapConfig.cancel')}
        confirmText={t('playgroundMapConfig.confirm')}
      />
    </div>
  );

  // ===== PC 端渲染 =====
  // 搜索内容
  const renderSearchContent = () => (
    <div className={styles['search-panel']}>
      <Input
        placeholder={t('playgroundMapConfig.searchPlaceholderPC')}
        value={searchKeyword}
        onChange={(e) => handlePCInputChange(e.target.value)}
        onCompositionStart={handlePCCompositionStart}
        onCompositionEnd={handlePCCompositionEnd}
        allowClear
        size="middle"
        maxLength={50}
        showCount
      />
      {/* 已选中操场回填展示 */}
      {selectedSearchItem && (
        <div className={styles['filter-footer']}>
          <div className={styles['filter-count']}>
            {t('playgroundMapConfig.selectedLabel')}
            <span className={styles['filter-count-num']}>{selectedSearchItem.pgName}</span>
          </div>
          <button className={styles['clear-btn']} onClick={handlePCClearSearch}>
            {t('playgroundMapConfig.clear')}
          </button>
        </div>
      )}
      {!isComposing && searchKeyword.trim() && !selectedSearchItem && isSearching && (
        <div className={styles['search-empty']}>{t('playgroundMapConfig.searching')}</div>
      )}
      {!isComposing &&
        searchKeyword.trim() &&
        !selectedSearchItem &&
        !isSearching &&
        searchDropdownVisible &&
        searchResults.length > 0 && (
          <div className={styles['search-results']}>
            {searchResults.map((item) => (
              <div key={item.pgNo} className={styles['search-item']} onClick={() => handlePCSearchResultClick(item)}>
                <div className={styles['search-item-name']}>{highlightKeyword(item.pgName, searchKeyword)}</div>
                <div className={styles['search-item-meta']}>
                  <span className={styles['search-item-address']}>{item.pgAddress}</span>
                  <span className={styles['search-item-status']}>· {item.pgStatusName}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      {!isComposing && searchKeyword.trim() && !selectedSearchItem && !isSearching && searchResults.length === 0 && (
        <div className={styles['search-empty']}>{t('playgroundMapConfig.noResult')}</div>
      )}
    </div>
  );

  // 地区筛选内容
  const renderRegionContent = () => (
    <div className={styles['region-panel']}>
      <Select
        placeholder={t('playgroundMapConfig.selectProvince')}
        value={selectedProvince?.adCode}
        onChange={handleProvinceChange}
        size="middle"
        className={styles['region-select']}
        allowClear
        onClear={handleClearRegion}
        getPopupContainer={() => panelRef.current || document.body}
      >
        {provinceOptions.map((p) => (
          <Select.Option key={p.adCode} value={p.adCode}>
            {p.name}
          </Select.Option>
        ))}
      </Select>
      <Select
        placeholder={t('playgroundMapConfig.selectCity')}
        value={selectedProvince ? selectedCity?.adCode || 'all' : undefined}
        onChange={handleCityChange}
        size="middle"
        className={styles['region-select']}
        disabled={!selectedProvince}
        loading={!!selectedProvince && cityOptions.length === 0}
        allowClear
        onClear={() => onCitySelect(null)}
        getPopupContainer={() => panelRef.current || document.body}
      >
        <Select.Option value="all">{t('playgroundMapConfig.allCities')}</Select.Option>
        {cityOptions.map((c) => (
          <Select.Option key={c.adCode} value={c.adCode}>
            {c.name}
          </Select.Option>
        ))}
      </Select>
      {selectedProvince && (
        <div className={styles['filter-footer']}>
          <div className={styles['filter-count']}>
            {getFilteredCount() === undefined ? (
              <span className={styles['filter-count-loading']}>{t('playgroundMapConfig.filtering')}</span>
            ) : (
              <span>
                {(t('playgroundMapConfig.filteredCount') as string).replace('{{count}}', String(getFilteredCount()))}
              </span>
            )}
          </div>
          <button className={styles['clear-btn']} onClick={handleClearRegion}>
            {t('playgroundMapConfig.clear')}
          </button>
        </div>
      )}
    </div>
  );

  // PC 浮窗
  const renderPopup = () => {
    if (!popupVisible) return null;
    return (
      <div className={styles.popup}>
        <div className={styles['segmented-bar']}>
          <div className={styles.segmented}>
            <button
              className={`${styles['seg-item']} ${mode === 'search' ? styles.active : ''}`}
              onClick={() => handleModeChange('search')}
            >
              {t('playgroundMapConfig.tabSearch')}
            </button>
            <button
              className={`${styles['seg-item']} ${mode === 'region' ? styles.active : ''}`}
              onClick={() => handleModeChange('region')}
            >
              {t('playgroundMapConfig.tabRegion')}
            </button>
          </div>
        </div>
        <div className={styles['popup-body']}>{mode === 'search' ? renderSearchContent() : renderRegionContent()}</div>
      </div>
    );
  };

  // PC 触发按钮
  const renderTrigger = () => (
    <button
      className={`${styles['trigger-btn']} ${popupVisible ? styles.active : ''} ${
        hasActiveFilter ? styles['has-filter'] : ''
      }`}
      onClick={() => setPopupVisible(!popupVisible)}
    >
      <span className={styles['trigger-label']}>{getTriggerLabel()}</span>
      <span className={`${styles['trigger-arrow']} ${popupVisible ? styles.open : ''}`}>
        <Arrow className={styles['arrow-icon']} />
      </span>
    </button>
  );

  const renderPC = () => (
    <div className={styles['filter-panel-pc']} ref={panelRef}>
      {renderTrigger()}
      {renderPopup()}
    </div>
  );

  return isMobile ? renderH5() : renderPC();
};

FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;
